import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import type { PerformanceResult } from '../../../../api/widgetTelemetry';

interface PerformanceProps {
  data: PerformanceResult | null;
  loading: boolean;
}

const EVENT_LABELS: Record<string, string> = {
  widget_time_to_interactive: 'Time to Interactive',
  location_resolve_time: 'Location Resolve',
  venue_detail_load_time: 'Venue Detail Load',
};

export default function Performance({ data, loading }: PerformanceProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-[#1a1f1c] rounded-lg p-4 border border-stone-200 dark:border-stone-700 animate-pulse h-48"
          />
        ))}
      </div>
    );
  }

  if (!data) return null;

  const chartData = data.events.map((e) => ({
    event: EVENT_LABELS[e.event] ?? e.event,
    p50: e.p50,
    p75: e.p75,
    p95: e.p95,
  }));

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 p-4">
        <h3 className="text-sm font-medium text-bark-900 dark:text-stone-100 mb-4">
          Performance percentiles (ms)
        </h3>
        {chartData.every((d) => d.p50 === 0 && d.p75 === 0 && d.p95 === 0) ? (
          <p className="text-xs text-stone-400 text-center py-8">No performance samples</p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="event" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} label={{ value: 'ms', angle: -90, position: 'insideLeft', style: { fontSize: 11 } }} />
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any -- Recharts v3 Tooltip formatter types are overly strict */}
              <Tooltip formatter={((value: number) => `${value.toLocaleString()} ms`) as any} />
              <Legend />
              <Bar dataKey="p50" name="P50" fill="#4A6741" radius={[4, 4, 0, 0]} />
              <Bar dataKey="p75" name="P75" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              <Bar dataKey="p95" name="P95" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="bg-white dark:bg-[#1a1f1c] rounded-lg border border-stone-200 dark:border-stone-700 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 dark:bg-[#1e2520]">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-stone-600 dark:text-stone-400">Event</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">Samples</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">P50</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">P75</th>
              <th className="px-4 py-3 text-right font-medium text-stone-600 dark:text-stone-400">P95</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
            {data.events.map((row) => (
              <tr key={row.event} className="hover:bg-moss-50/50 dark:hover:bg-[#242d28]">
                <td className="px-4 py-2 text-bark-900 dark:text-stone-200">
                  {EVENT_LABELS[row.event] ?? row.event}
                </td>
                <td className="px-4 py-2 text-right">{row.sampleCount.toLocaleString()}</td>
                <td className="px-4 py-2 text-right">{row.p50.toLocaleString()} ms</td>
                <td className="px-4 py-2 text-right">{row.p75.toLocaleString()} ms</td>
                <td className="px-4 py-2 text-right">{row.p95.toLocaleString()} ms</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
